import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { Smartphone, Download, Share2, PlusSquare, X, CheckCircle2 } from 'lucide-react';
import { AuthContext } from '../App';

/**
 * Widget para instalar IBA Connect como PWA.
 * Solo se muestra si el admin lo activó vía /api/admin/banner-pwa-config.
 * Comportamiento:
 *   - En Chrome/Edge/Android: escucha `beforeinstallprompt` y muestra
 *     un botón "Instalar app" que dispara el cuadro nativo del sistema.
 *   - En iPhone/iPad (Safari/Chrome): muestra un mini-tutorial paso-a-paso
 *     con icono de Compartir → "Añadir a pantalla de inicio".
 *   - Si la app YA está instalada (modo standalone), muestra una tarjeta
 *     verde de confirmación.
 */
const InstallPWAWidget = () => {
  const { API } = useContext(AuthContext);
  const [activo, setActivo] = useState(null); // null = aún cargando
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);

  // Detectores
  const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent || '');
  const isStandalone = typeof window !== 'undefined' && (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator?.standalone === true
  );

  // Consultar al backend si el admin activó el banner
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/public/banner-pwa-config`);
        if (!cancelled) setActivo(!!res.data?.activo);
      } catch (_e) {
        if (!cancelled) setActivo(false);
      }
    })();
    return () => { cancelled = true; };
  }, [API]);

  useEffect(() => {
    if (isStandalone) {
      setInstalled(true);
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);

    const installedHandler = () => setInstalled(true);
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, [isStandalone]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const result = await deferredPrompt.userChoice;
        if (result?.outcome === 'accepted') {
          setInstalled(true);
        }
        setDeferredPrompt(null);
      } catch (_e) {
        // Si el browser cancela, no hacemos nada
      }
    } else if (isIOS) {
      setShowIosHelp(true);
    }
  };

  // No mostrar nada hasta saber si está activo, ni si está apagado
  if (activo === null || activo === false) return null;

  // Si ya está instalada → tarjeta verde con check
  if (installed) {
    return (
      <div
        className="bg-gradient-to-br from-emerald-500 via-green-500 to-teal-500 rounded-2xl p-5 text-white shadow-lg border border-emerald-300/50"
        data-testid="pwa-installed-widget"
      >
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shrink-0">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <div className="min-w-0">
            <h3 className="font-bold text-lg">App instalada</h3>
            <p className="text-sm text-white/85">
              Estás usando IBA Connect como aplicación. ¡Genial!
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Solo mostrar el widget si tenemos prompt nativo o si es iOS (que no lo tiene)
  const puedeInstalar = !!deferredPrompt || isIOS;
  if (!puedeInstalar) {
    // Muestra una versión "siempre visible" pero más sutil con instrucciones
    // de respaldo para navegadores que aún no han disparado el evento (toma
    // unos segundos en algunos browsers, o requiere visita previa).
    return (
      <div
        className="bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-200 rounded-2xl p-5 shadow-sm"
        data-testid="pwa-widget-fallback"
      >
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center shrink-0">
            <Smartphone className="w-8 h-8 text-white" />
          </div>
          <div className="min-w-0">
            <h3 className="font-bold text-gray-900">Instalá IBA Connect en tu dispositivo</h3>
            <p className="text-sm text-gray-600 mt-1">
              Desde el menú de tu navegador (⋮) elegí
              <span className="font-semibold"> "Instalar app" </span>
              o <span className="font-semibold">"Agregar a pantalla de inicio"</span>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className="bg-gradient-to-br from-indigo-600 via-blue-600 to-cyan-500 rounded-2xl p-5 text-white shadow-lg relative overflow-hidden"
        data-testid="pwa-install-widget"
      >
        {/* Decoración */}
        <div className="absolute -right-6 -top-6 w-28 h-28 bg-white/10 rounded-full" />
        <div className="absolute -right-2 -bottom-8 w-20 h-20 bg-white/10 rounded-full" />

        <div className="relative flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shrink-0">
            <Smartphone className="w-8 h-8" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-lg">Instalá IBA Connect</h3>
            <p className="text-sm text-white/85 mt-1 leading-snug">
              Tenela como app en tu celular o computadora. Más rápida y sin barras del navegador.
            </p>
            <button
              type="button"
              onClick={handleInstall}
              className="mt-3 inline-flex items-center gap-2 bg-white text-indigo-700 hover:bg-indigo-50 font-bold text-sm px-4 py-2 rounded-xl shadow transition-all active:scale-95"
              data-testid="install-pwa-btn"
            >
              <Download className="w-4 h-4" />
              {isIOS ? 'Cómo instalar en iPhone' : 'Instalar app'}
            </button>
          </div>
        </div>
      </div>

      {/* Modal tutorial iOS */}
      {showIosHelp && (
        <div
          className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowIosHelp(false)}
          data-testid="ios-install-help"
        >
          <div
            className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Instalar en iPhone</h3>
              <button
                onClick={() => setShowIosHelp(false)}
                className="p-1 rounded-full hover:bg-gray-100"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <ol className="space-y-3 text-sm text-gray-700">
              <li className="flex gap-3 items-start">
                <span className="w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">1</span>
                <span>Tocá el botón <Share2 className="inline w-4 h-4 align-text-bottom" /> <strong>Compartir</strong> abajo en Safari (o en la barra superior de Chrome).</span>
              </li>
              <li className="flex gap-3 items-start">
                <span className="w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">2</span>
                <span>Bajá y tocá <PlusSquare className="inline w-4 h-4 align-text-bottom" /> <strong>"Añadir a pantalla de inicio"</strong>.</span>
              </li>
              <li className="flex gap-3 items-start">
                <span className="w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">3</span>
                <span>Tocá <strong>"Añadir"</strong> arriba a la derecha. Listo: aparece el icono de IBA Connect en tu pantalla de inicio.</span>
              </li>
            </ol>
            <button
              onClick={() => setShowIosHelp(false)}
              className="mt-5 w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold transition-colors"
            >
              Entendido
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallPWAWidget;
